import { BrowserRouter, unstable_HistoryRouter as HistoryRouter, Routes, Route, Navigate, useParams } from 'react-router';
import type { ReactNode } from 'react';
import { FluentProvider, Toaster } from '@fluentui/react-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { AppShell }       from './components/layout/AppShell';
import { ErrorBoundary }  from './components/auth/ErrorBoundary';
import { LoginPage }      from './pages/LoginPage';
import { DemoLoginPage }  from './pages/DemoLoginPage';
import { tenantCodeFromHost } from './utils/host-tenant';
import { createTenantHostHistory } from './utils/tenant-host-history';
import { RoleSelectionPage } from './pages/RoleSelectionPage';
import { AccountInactivePage } from './pages/AccountInactivePage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { LegalPage } from './pages/LegalPage';
import RegisterPage from './pages/RegisterPage';
import { DashboardPage }  from './pages/DashboardPage';
import { InvestorOverviewPage } from './pages/InvestorOverviewPage';
import { InvestorYieldPage } from './pages/InvestorYieldPage';
import { InvestorLogisticsPage } from './pages/InvestorLogisticsPage';
import { InvestorModulesPage } from './pages/InvestorModulesPage';
import { InvestorModuleDetailPage } from './pages/InvestorModuleDetailPage';
import { InvestorFundingPage } from './pages/InvestorFundingPage';
import { CostosPage } from './pages/CostosPage';
import { InvestorProductionPage } from './pages/InvestorProductionPage';
import { FieldPage }      from './pages/FieldPage';
import { LoteDetailPage } from './pages/LoteDetailPage';
import { FinancePage }    from './pages/FinancePage';
import { SalesPage }      from './pages/SalesPage';
import { UsersPage }      from './pages/UsersPage';
import { IrrigationPage } from './pages/IrrigationPage';
import { HarvestPage }    from './pages/HarvestPage';
import { InventoryPage }  from './pages/InventoryPage';
import { MachineryPage }  from './pages/MachineryPage';
import { DronesPage } from './pages/DronesPage';
import { TasksPage }      from './pages/TasksPage';
import { IncompleteDraftsPage } from './pages/IncompleteDraftsPage';
import { ExpenseApprovalsPage } from './pages/ExpenseApprovalsPage';
import { CalculadorasPage } from './pages/CalculadorasPage';
import { ExpensesDashboardPage } from './pages/ExpensesDashboardPage';
import { SprayPage }      from './pages/SprayPage';
import { PorcinoPage }    from './pages/PorcinoPage';
import { PayrollPage }    from './pages/PayrollPage';
import { ReportsPage }    from './pages/ReportsPage';
import { MapPage }        from './pages/MapPage';
import { ChatPage }       from './pages/ChatPage';
import { MermaPage }      from './pages/MermaPage';
import { CropPage }       from './pages/CropPage';
import { ExportPage }     from './pages/ExportPage';
import { TransportPage }  from './pages/TransportPage';
import ShipmentEvidencePage from './pages/ShipmentEvidencePage';
import SharedEvidencePage from './pages/SharedEvidencePage';
import { TraceabilityPage } from './pages/TraceabilityPage';
import TraceabilityPlanPage from './pages/TraceabilityPlanPage';
import FsmaExportPage from './pages/FsmaExportPage';
import { ProtocolPage }     from './pages/ProtocolPage';
import { SuperAdminPage }   from './pages/SuperAdminPage';
import { RanchosAdminPage } from './pages/RanchosAdminPage';
import { AdminConfigPage }  from './pages/AdminConfigPage';
import { PermissionsMatrixPage } from './pages/PermissionsMatrixPage';
import { BulkImportPage }   from './pages/BulkImportPage';
import { InventoryUploadPage } from './pages/InventoryUploadPage';
import { VocabularyPage } from './pages/VocabularyPage';
import { DevicesPage }    from './pages/DevicesPage';
import { SystemPage }     from './pages/SystemPage';
import { GettingStartedPage } from './pages/GettingStartedPage';
import { DocumentsPage }  from './pages/DocumentsPage';
import { EvidencePage } from './pages/EvidencePage';
import { PersonnelPage } from './pages/PersonnelPage';
import { GatePage } from './pages/GatePage';
import { AttendancePage } from './pages/AttendancePage';
import { RiesgosPage }    from './pages/RiesgosPage';
import { ComplianceRulesPage } from './pages/ComplianceRulesPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { agrovidLightTheme, agrovidDarkTheme } from './theme/agrovid-theme';
import { useThemeStore }  from './store/theme.store';
import { useAuthStore }   from './store/auth.store';
import { useTenantStore } from './store/tenant.store';
import { resolveDefaultRoute, pickLandingPath } from './utils/defaultRoute';
import { usePermissions } from './hooks/usePermission';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, gcTime: 5 * 60_000, retry: 1, refetchOnWindowFocus: true },
  },
});

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

/**
 * El rol INVESTOR aterriza en el resumen ejecutivo de solo lectura en lugar
 * del DashboardPage operativo (que expone acciones de escritura como
 * completar tareas — no apropiado para ese rol).
 */
function DefaultLandingRedirect() {
  const activeRole = useAuthStore(s => s.user?.activeRole);
  const { permissions, isLoading } = usePermissions();
  // Espera la matriz: sin ella todo permiso parece ausente y se aterrizaba en una pantalla equivocada.
  if (isLoading) return null;
  return <Navigate to={pickLandingPath(permissions, activeRole)} replace />;
}

/**
 * En un subdominio de empresa (<código>.agrovid.solutions) la empresa sale del host y la URL
 * no lleva /<empresa>: el historial traduce entre la URL del navegador y las rutas internas
 * /:tenantId/... (ver utils/tenant-host-history.ts). En cualquier otro host, router normal.
 */
const hostTenantCode = tenantCodeFromHost();
const hostHistory = hostTenantCode ? createTenantHostHistory(hostTenantCode) : null;

function AppRouter({ children }: { children: ReactNode }) {
  return hostHistory
    ? <HistoryRouter history={hostHistory}>{children}</HistoryRouter>
    : <BrowserRouter>{children}</BrowserRouter>;
}

/** Raíz y rutas desconocidas: a la sesión activa si existe; si no, al login de la empresa. */
function DefaultRedirect() {
  const token = useAuthStore(s => s.token);
  const user = useAuthStore(s => s.user);
  const knownTenantCode = useTenantStore(s => s.tenantId);
  // En un subdominio de empresa manda el host; fuera de él, la empresa recordada.
  return <Navigate to={resolveDefaultRoute(token, user, hostTenantCode ?? knownTenantCode)} replace />;
}

/** Alias de transición: `investor/modules/:moduleId` → `fincas/modulo/:moduleId` (Fase A, §3.1). */
function InvestorModulesRedirect() {
  const { moduleId } = useParams<{ moduleId: string }>();
  return <Navigate to={`../fincas/modulo/${moduleId}`} replace />;
}

function ThemedApp() {
  const { mode } = useThemeStore();
  const theme = mode === 'dark' ? agrovidDarkTheme : agrovidLightTheme;

  return (
    <FluentProvider theme={theme}>
      <Toaster toasterId="agrovid-toaster" position="top-end" />
      <QueryClientProvider client={queryClient}>
        <AppRouter>
          <Routes>
            {/* Public — sin tenant */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/evidencia-compartida" element={<SharedEvidencePage />} />
            <Route path="/account-inactive" element={<AccountInactivePage />} />
            {/* F0.10 — link de invitación: público, el tenant lo dice la invitación */}
            <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
            {/* F0 — self-registration onboarding: público, resuelve tenant vía ?code= */}
            <Route path="/register" element={<RegisterPage />} />

            {/* Públicas: textos legales del footer (borrador pendiente de revisión legal) */}
            <Route path="/legal/:doc" element={<LegalPage />} />

            {/* Public — con tenant */}
            <Route path="/demo/login"            element={<DemoLoginPage />} />
            <Route path="/:tenantId/login"       element={<LoginPage />} />
            <Route path="/:tenantId/select-role" element={<RoleSelectionPage />} />

            {/* Protected — siempre con /:tenantId */}
            <Route path="/:tenantId" element={<AppShell />}>
              <Route index element={<DefaultLandingRedirect />} />
              <Route path="dashboard"     element={<ProtectedRoute requiredPermission="dashboard:read"><DashboardPage /></ProtectedRoute>} />
              <Route path="investor/overview" element={<ProtectedRoute requiredPermission="dashboard:read"><InvestorOverviewPage /></ProtectedRoute>} />
              <Route path="rendimiento" element={<ProtectedRoute requiredPermission="harvest:read"><InvestorYieldPage /></ProtectedRoute>} />
              <Route path="logistica" element={<ProtectedRoute requiredPermission="export:read"><InvestorLogisticsPage /></ProtectedRoute>} />
              <Route path="fincas" element={<ProtectedRoute requiredPermission="projects:read"><InvestorModulesPage /></ProtectedRoute>} />
              <Route path="fincas/modulo/:moduleId" element={<ProtectedRoute requiredPermission="projects:read"><InvestorModuleDetailPage /></ProtectedRoute>} />
              <Route path="inversion" element={<ProtectedRoute requiredPermission="finance:read"><InvestorFundingPage /></ProtectedRoute>} />
              <Route path="costos" element={<ProtectedRoute requiredPermission="finance:read"><CostosPage /></ProtectedRoute>} />
              <Route path="produccion" element={<ProtectedRoute requiredPermission="production:read"><InvestorProductionPage /></ProtectedRoute>} />
              {/* Alias de transición — rutas investor/* viejas, bookmarks/enlaces externos existentes (Fase A, §3.1). Retirar en una limpieza futura. */}
              <Route path="investor/yield" element={<Navigate to="../rendimiento" replace />} />
              <Route path="investor/logistics" element={<Navigate to="../logistica" replace />} />
              <Route path="investor/modules" element={<Navigate to="../fincas" replace />} />
              <Route path="investor/modules/:moduleId" element={<InvestorModulesRedirect />} />
              <Route path="investor/funding" element={<Navigate to="../inversion" replace />} />
              <Route path="investor/production" element={<Navigate to="../produccion" replace />} />
              <Route path="campo"         element={<FieldPage />} />
              <Route path="campo/:loteId" element={<LoteDetailPage />} />
              <Route path="finanzas"      element={<ProtectedRoute requiredPermission="finance:read"><FinancePage /></ProtectedRoute>} />
              <Route path="ventas"        element={<ProtectedRoute requiredPermission="sales:read"><SalesPage /></ProtectedRoute>} />
              <Route path="riego"         element={<IrrigationPage />} />
              <Route path="cosecha"       element={<HarvestPage />} />
              <Route path="inventario"    element={<ProtectedRoute requiredPermission="inventory:read"><InventoryPage /></ProtectedRoute>} />
              <Route path="inventarios"   element={<ProtectedRoute requiredPermission="inventory:write"><InventoryUploadPage /></ProtectedRoute>} />
              <Route path="usuarios"      element={<ProtectedRoute requiredPermission="users:read"><UsersPage /></ProtectedRoute>} />
              <Route path="super-admin"   element={<ProtectedRoute requiredPermission="admin:super"><SuperAdminPage /></ProtectedRoute>} />
              {/* F0-unify: super-admin/config absorbió el tab de Idioma que vivía en /ajustes
                  (SettingsPage, eliminada) — el permiso baja a dashboard:read para que
                  cualquier usuario llegue al tab de Idioma; los demás tabs siguen gateados
                  por rol SUPER_ADMIN dentro del propio componente. */}
              <Route path="super-admin/config" element={<ProtectedRoute requiredPermission="dashboard:read"><AdminConfigPage /></ProtectedRoute>} />
              <Route path="ajustes"       element={<Navigate to="../super-admin/config" replace />} />
              <Route path="tareas"        element={<TasksPage />} />
              <Route path="registros-incompletos" element={<IncompleteDraftsPage />} />
              <Route path="calculadoras" element={<ProtectedRoute requiredPermission="calculators:read"><CalculadorasPage /></ProtectedRoute>} />
              <Route path="gastos" element={<ProtectedRoute requiredPermission="expenses:read"><ExpensesDashboardPage /></ProtectedRoute>} />
              <Route path="gastos-aprobaciones" element={<ProtectedRoute requiredPermission="expenses:approve"><ExpenseApprovalsPage /></ProtectedRoute>} />
              <Route path="maquinaria"    element={<ProtectedRoute requiredPermission="machinery:read"><MachineryPage /></ProtectedRoute>} />
              <Route path="drones"        element={<ProtectedRoute requiredPermission="machinery:read"><DronesPage /></ProtectedRoute>} />
              <Route path="aplicacion"    element={<SprayPage />} />
              <Route path="porcino"       element={<PorcinoPage />} />
              <Route path="nomina"        element={<ProtectedRoute requiredPermission="payroll:read"><PayrollPage /></ProtectedRoute>} />
              <Route path="reportes"      element={<ProtectedRoute requiredPermission="reports:read"><ReportsPage /></ProtectedRoute>} />
              <Route path="mapa"          element={<MapPage />} />
              <Route path="chat"          element={<ChatPage />} />
              <Route path="merma"         element={<MermaPage />} />
              <Route path="cultivo"       element={<ProtectedRoute requiredPermission="crop:read"><CropPage /></ProtectedRoute>} />
              <Route path="exportacion"   element={<ProtectedRoute requiredPermission="export:read"><ExportPage /></ProtectedRoute>} />
              <Route path="transporte"    element={<ProtectedRoute requiredPermission="transport:read"><TransportPage /></ProtectedRoute>} />
              <Route path="transporte/:shipmentId/evidencia" element={<ProtectedRoute requiredPermission="transport:read"><ShipmentEvidencePage /></ProtectedRoute>} />
              <Route path="trazabilidad"  element={<ProtectedRoute requiredPermission="traceability:read"><TraceabilityPage /></ProtectedRoute>} />
              <Route path="trazabilidad/plan" element={<ProtectedRoute requiredPermission="traceability:read"><TraceabilityPlanPage /></ProtectedRoute>} />
              <Route path="trazabilidad/fsma" element={<ProtectedRoute requiredPermission="traceability:read"><FsmaExportPage /></ProtectedRoute>} />
              <Route path="protocolos"    element={<ProtectedRoute requiredPermission="protocol:read"><ProtocolPage /></ProtectedRoute>} />
              <Route path="ranchos"       element={<ProtectedRoute requiredPermission="admin:ranchos"><RanchosAdminPage /></ProtectedRoute>} />
              <Route path="vocabulario"   element={<ProtectedRoute requiredPermission="vocabulary:write"><VocabularyPage /></ProtectedRoute>} />
              <Route path="dispositivos"  element={<ProtectedRoute requiredPermission="devices:read"><DevicesPage /></ProtectedRoute>} />
              <Route path="permisos"      element={<ProtectedRoute requiredPermission="admin:rbac"><PermissionsMatrixPage /></ProtectedRoute>} />
              <Route path="primeros-pasos" element={<ProtectedRoute requiredPermission="dashboard:read"><GettingStartedPage /></ProtectedRoute>} />
              <Route path="sistema"      element={<ProtectedRoute requiredPermission="dashboard:read"><SystemPage /></ProtectedRoute>} />
              <Route path="bulk-import"   element={<ProtectedRoute requiredPermission="admin:bulk-import"><BulkImportPage /></ProtectedRoute>} />
              <Route path="documentos"    element={<ProtectedRoute requiredPermission="reports:read"><DocumentsPage /></ProtectedRoute>} />
              <Route path="evidencias"    element={<ProtectedRoute requiredPermission="audit:read"><EvidencePage /></ProtectedRoute>} />
              <Route path="personal"      element={<ProtectedRoute requiredPermission="attendance:read"><PersonnelPage /></ProtectedRoute>} />
              <Route path="caseta"        element={<ProtectedRoute requiredPermission="attendance:gate"><GatePage /></ProtectedRoute>} />
              <Route path="asistencia"    element={<ProtectedRoute requiredPermission="attendance:read"><AttendancePage /></ProtectedRoute>} />
              <Route path="riesgos"       element={<ProtectedRoute requiredPermission="anomalies:read"><RiesgosPage /></ProtectedRoute>} />
              <Route path="reglas-cumplimiento" element={<ProtectedRoute requiredPermission="compliance:read"><ComplianceRulesPage /></ProtectedRoute>} />
            </Route>

            {/* Redirects */}
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
        </AppRouter>
      </QueryClientProvider>
    </FluentProvider>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ThemedApp />
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}
